"use client"

import getDomains from "@/services/domain-services"
import { DomainEntity } from "@/types/domain"
import { Select, Typography } from "antd"
import { useEffect, useMemo, useState } from "react"
import { create } from "zustand"

interface WebsiteDomainStore {
    websiteDomain: string
    setWebsiteDomain: (domain: string) => void
}

export const useWebsiteDomainStore = create<WebsiteDomainStore>((set) => ({
    websiteDomain: "",
    setWebsiteDomain: (domain) => set({ websiteDomain: domain }),
}))

export interface WebsiteSelectProps {
    value?: string
    onChange: (value: string) => void
    label?: string
    placeholder?: string
}

export function WebsiteSelect({
    value,
    onChange,
    label = "Select Website",
    placeholder = "Select Website",
}: WebsiteSelectProps) {
    const [domains, setDomains] = useState<DomainEntity[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const setWebsiteDomain = useWebsiteDomainStore((state) => state.setWebsiteDomain)

    useEffect(() => {
        const fetchDomains = async () => {
            try {
                setLoading(true)
                setError(null)
                const data = await getDomains()
                setDomains(data || [])
            } catch (err) {
                console.error("Failed to fetch domains:", err)
                setError("Failed to load websites")
                setDomains([])
            } finally {
                setLoading(false)
            }
        }
        fetchDomains()
    }, [])

    useEffect(() => {
        if (value) {
            setWebsiteDomain(value)
        }
    }, [value, setWebsiteDomain])

    const options = useMemo(
        () =>
            domains.map((domain) => ({
                value: domain.attributes?.domain || "",
                label: `${domain.attributes?.name || ""} (${domain.attributes?.domain || ""})`,
            })),
        [domains]
    )

    const handleChange = (newValue: string) => {
        setWebsiteDomain(newValue)
        onChange(newValue)
    }

    return (
        <div>
            <Typography.Title level={5}>{label}</Typography.Title>
            {error && (
                <Typography.Text type="danger" className="block mb-2">
                    {error}
                </Typography.Text>
            )}
            <Select
                value={value}
                onChange={handleChange}
                options={options}
                className="w-full"
                placeholder={placeholder}
                showSearch
                loading={loading}
                filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
            />
        </div>
    )
}

